import { TransactionStatus } from '@varo/graphql-schema-types';
import { useGlobalSearchParams, usePathname, useRouter } from 'expo-router';
import NoTransactions from 'features/accounts/transactions/NoTransactions/NoTransactions';
import { MY_VARO } from 'features/myVaro/constants/constants';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';
import { formatDisplayDate } from 'shared/utils/string/date';

import useExternalTransactions from './hooks/useExternalTransactions';

export interface ExternalTransactionsProps {
  accountId: string;
  accountType?: string;
  first: number;
}

export interface ExternalAccountChildRef {
  refetchExternalTransactions: () => void;
}

const ExternalTransactions = forwardRef<
  ExternalAccountChildRef,
  ExternalTransactionsProps
>(({ accountId, first, accountType }: ExternalTransactionsProps, ref) => {
  const router = useRouter();
  const {
    externalTransactionsLoading,
    externalTransactions,
    externalTransactionsError,
    loadingMoreData,
    refetchExternalTransactions,
  } = useExternalTransactions({ accountId, first });

  const { id: externalAccountId } = useGlobalSearchParams<{ id: string }>();
  const urlPath = usePathname();
  const redirectedFromMyvaro = urlPath?.includes(MY_VARO);

  const { t: translatedText } = useTranslation('accounts');

  useImperativeHandle(ref, () => ({
    refetchExternalTransactions,
  }));

  if (externalTransactionsLoading && !externalTransactions) {
    return (
      <View style={styles.containerCentered}>
        <LoadingSpinner />
      </View>
    );
  }
  //TODO: add overlay component to cover Loading & error state
  if (externalTransactionsError) {
    // TODO: replace the error message with a localized string and the new ui provided when available
    return <BodyBaseText numberOfLines={0}>Found Error </BodyBaseText>;
  }

  const handleOnClick = (transactionId: string) => {
    router.push({
      pathname: redirectedFromMyvaro
        ? '/myvaro/external-accounts/[id]/transactions/[transactionId]'
        : '/accounts/transaction-details',
      params: { id: externalAccountId, transactionId, accountType },
    });
  };
  const hasTransactions = Boolean(externalTransactions?.length);

  return hasTransactions ? (
    <View>
      {externalTransactions?.map((item, index) => {
        return (
          <View key={item.node.id}>
            {index > 0 ? (
              <View style={styles.divider}>
                <HairLineDivider />
              </View>
            ) : null}
            <AmplitudeMask>
              <TransactionListItem
                amountStyle="NEUTRAL"
                isPending={item.node.status === TransactionStatus.Pending}
                amountText={item.node.value.formatted}
                titleText={item.node.shortDescription}
                secondaryText={formatDisplayDate(new Date(item.node.createdAt))}
                onPress={() => handleOnClick(item?.node?.id)}
                analyticsId="transactionViewed"
                customAttributes={{
                  category: 'External',
                  account_type: accountType as string,
                }}
              />
            </AmplitudeMask>
          </View>
        );
      })}
      {loadingMoreData ? (
        <View style={styles.containerCentered}>
          <LoadingSpinner />
        </View>
      ) : null}
    </View>
  ) : (
    <NoTransactions title={translatedText('transactions.recentTransactions')} />
  );
});

const styles = StyleSheet.create({
  containerCentered: {
    alignItems: 'center',
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default ExternalTransactions;
